<template>
  <div class="report-page">
    <div class="filter-card">
      <div class="fc-row fc-row-primary">
        <div class="field">
          <label class="field-lbl">ช่วงวันที่ (ความเคลื่อนไหว)</label>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            format="DD MMM YY"
            :shortcuts="dateRangeShortcuts"
            :disabled-date="notAfterToday"
            placeholder="เลือกวันที่ต้องการค้นหา"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
        </div>

        <b-button variant="primary" class="btn-primary-lg" @click="fetchReport">
          แสดงข้อมูล
        </b-button>
      </div>
    </div>

    <div id="report-elements">
      <div class="toolbar">
        <span></span>
        <div class="toolbar-right">
          <b-dropdown size="sm" variant="outline-success" right :disabled="!movement">
            <template #button-content>
              <i class="fas fa-download"></i>
            </template>
            <b-dropdown-item @click="excelExport">
              <i class="fas fa-file-excel mr-2"></i>Excel
            </b-dropdown-item>
            <b-dropdown-item @click="pdfExport">
              <i class="fas fa-file-pdf mr-2"></i>PDF
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <!-- Block 1: การ์ดสรุป 4 ใบ — same four headline numbers as the clinic tab,
           but scoped to this one branch's movement totals. -->
      <b-row class="deposit-summary-cards mb-3" no-gutters>
        <b-col cols="6" md="3" class="px-1 mb-2">
          <b-card class="summary-tile h-100" body-class="text-center py-2">
            <div class="tile-label">รับมัดจำ</div>
            <div class="tile-num">{{ formatNumber(movementTotals.deposited) }}</div>
          </b-card>
        </b-col>
        <b-col cols="6" md="3" class="px-1 mb-2">
          <b-card class="summary-tile h-100" body-class="text-center py-2">
            <div class="tile-label">ตัดใช้มัดจำ</div>
            <div class="tile-num">{{ formatNumber(movementTotals.deducted) }}</div>
          </b-card>
        </b-col>
        <b-col cols="6" md="3" class="px-1 mb-2">
          <b-card class="summary-tile h-100" body-class="text-center py-2">
            <div class="tile-label">ยกเลิกรับมัดจำ</div>
            <div class="tile-num">{{ formatNumber(movementTotals.cancelledDeposit) }}</div>
          </b-card>
        </b-col>
        <b-col cols="6" md="3" class="px-1 mb-2">
          <b-card class="summary-tile h-100" body-class="text-center py-2">
            <div class="tile-label">คืนจากยกเลิก</div>
            <div class="tile-num">{{ formatNumber(movementTotals.restoredDeduction) }}</div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Block 2: ตารางความเคลื่อนไหวของสาขานี้ — always exactly one row, rendered
           as a normal table (not collapsed into cards) so the export shape matches
           the clinic tab. There is no opening/closing balance column here: a deposit
           balance is clinic-wide per patient and cannot be meaningfully split by
           branch, so the branch API does not return one — do not derive it. -->
      <b-table
        id="DepositMovementTable"
        ref="DepositMovementTable"
        class="my-0 report-table"
        small
        bordered
        hover
        head-variant="light"
        :items="movementRows"
        :fields="movementFields"
        :responsive="true"
        show-empty
      >
        <template #cell(deposited)="row">{{ formatNumber(row.value) }}</template>
        <template #cell(deducted)="row">{{ formatNumber(row.value) }}</template>
        <template #cell(cancelledDeposit)="row">{{ formatNumber(row.value) }}</template>
        <template #cell(restoredDeduction)="row">{{ formatNumber(row.value) }}</template>
        <template #cell(net)="row">{{ formatNumber(row.value) }}</template>
        <template #cell(patientCount)="row">{{ formatInt(row.value) }}</template>
        <template #empty="">
          <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
        </template>
      </b-table>
      <p class="text-muted small mt-1 mb-4">
        หมายเหตุ: ตัวเลขนี้คือความเคลื่อนไหวของสาขานี้เท่านั้น ไม่มียอดคงเหลือรายสาขา
        เพราะกระเป๋าเงินมัดจำผูกกับคนไข้ทั้งคลินิก ไม่ได้แยกตามสาขา
      </p>

      <!-- Block 5: ตารางรายการ ledger — server-side pagination via count/offset/limit -->
      <div class="toolbar">
        <h6 class="section-title mb-0">รายการ</h6>
        <div class="toolbar-right">
          <b-pagination
            v-model="linesPage"
            :total-rows="linesCount"
            :per-page="linesPerPage"
            size="sm"
            first-number
            last-number
            class="mb-0"
            @change="loadLinesPage"
          ></b-pagination>
        </div>
      </div>
      <b-table
        id="DepositLinesTable"
        ref="DepositLinesTable"
        class="my-0 report-table"
        small
        hover
        head-variant="light"
        :busy="linesBusy"
        :items="linesRows"
        :fields="linesFields"
        :responsive="true"
        show-empty
      >
        <template #cell(creationDt)="row">{{ formatDateTime(row.value) }}</template>
        <template #cell(type)="row">
          <b-badge :variant="typeVariant(row.value)">{{ typeLabel(row.value) }}</b-badge>
        </template>
        <template #cell(amount)="row">{{ formatNumber(row.value) }}</template>
        <template #cell(balanceAfter)="row">{{ formatNumber(row.value) }}</template>
        <template #empty="">
          <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
        </template>
      </b-table>
    </div>

    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";

// One map used everywhere a transaction type is shown on screen or exported.
const TYPE_LABEL = {
  DEPOSIT: "รับมัดจำ",
  DEDUCTION: "ตัดใช้มัดจำ",
  CANCEL_DEPOSIT: "ยกเลิกรับมัดจำ",
  CANCEL_DEDUCTION: "คืนจากยกเลิก",
  ADJUSTMENT: "ปรับยอด",
};

const TYPE_VARIANT = {
  DEPOSIT: "success",
  DEDUCTION: "danger",
  CANCEL_DEPOSIT: "warning",
  CANCEL_DEDUCTION: "info",
  ADJUSTMENT: "secondary",
};

export default {
  name: "BranchDepositReport",
  components: {
    Dialog,
    Loading,
  },
  data() {
    return {
      isLoading: false,
      reportTitle: "รายงานเงินมัดจำ (สาขา)",
      reportNameEn: "BranchDepositReport",

      dateRange: [],

      // Raw API response — kept whole so totals stay easy to reach.
      movement: null,

      linesRows: [],
      linesCount: 0,
      linesPage: 1,
      linesPerPage: 50,
      linesBusy: false,

      movementFields: [
        { key: "branchName", label: "สาขา", class: "narrow-spacing" },
        { key: "deposited", label: "รับมัดจำ", class: "text-right" },
        { key: "deducted", label: "ตัดใช้มัดจำ", class: "text-right" },
        { key: "cancelledDeposit", label: "ยกเลิกรับ", class: "text-right" },
        { key: "restoredDeduction", label: "คืนจากยกเลิก", class: "text-right" },
        { key: "net", label: "สุทธิ", class: "text-right" },
        { key: "patientCount", label: "คนไข้ที่มีความเคลื่อนไหว", class: "text-right" },
      ],
      linesFields: [
        { key: "creationDt", label: "วันที่", class: "narrow-spacing" },
        { key: "patientName", label: "คนไข้", class: "narrow-spacing" },
        { key: "dn", label: "HN", class: "narrow-spacing" },
        { key: "type", label: "ประเภท", class: "text-center" },
        { key: "receiptNo", label: "เลขที่ใบเสร็จ", class: "narrow-spacing" },
        { key: "amount", label: "จำนวน", class: "text-right" },
        { key: "balanceAfter", label: "คงเหลือหลังรายการ", class: "text-right" },
        { key: "note", label: "หมายเหตุ", class: "narrow-spacing" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      getReportDateRange: "moduleReport/getReportDateRange",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      branchInfo: "moduleBranch/getBranchInfo",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    movementTotals() {
      return (
        (this.movement && this.movement.totals) || {
          openingBalance: 0,
          deposited: 0,
          deducted: 0,
          cancelledDeposit: 0,
          restoredDeduction: 0,
          closingBalance: 0,
          patientCount: 0,
          txnCount: 0,
        }
      );
    },
    // Always exactly one row (this branch) — the branch API never returns
    // openingBalance/closingBalance per row because that would imply a
    // branch-level balance, which does not exist.
    movementRows() {
      return (this.movement && this.movement.branches) || [];
    },
    today() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
  },
  methods: {
    ...mapActions({
      fetchBranchDepositMovementReport: "moduleReport/fetchBranchDepositMovementReport",
      fetchBranchDepositLinesReport: "moduleReport/fetchBranchDepositLinesReport",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
    }),
    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) return "";
      return Number(value).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatInt(value) {
      if (value === null || value === undefined || isNaN(value)) return "0";
      return Number(value).toLocaleString("th-TH");
    },
    formatDateTime(date) {
      return date ? moment(date).locale("th").format("D MMM YY HH:mm") : "";
    },
    typeLabel(type) {
      return TYPE_LABEL[type] || type;
    },
    typeVariant(type) {
      return TYPE_VARIANT[type] || "secondary";
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
    rangeParams() {
      return {
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        startDt: moment(this.dateRange[0]).startOf("day").toISOString(),
        // endDt is exclusive: the day after dateTo at 00:00
        endDt: moment(this.dateRange[1]).add(1, "day").startOf("day").toISOString(),
      };
    },
    async fetchReport() {
      if (!this.dateRange[0] || !this.dateRange[1]) return;
      this.setStateReportDateRange(this.dateRange);

      this.isLoading = true;
      this.linesPage = 1;
      const params = this.rangeParams();
      try {
        const [movementRes, linesRes] = await Promise.all([
          this.fetchBranchDepositMovementReport(params),
          this.fetchBranchDepositLinesReport({ ...params, offset: 0, limit: this.linesPerPage }),
        ]);
        this.movement = movementRes.data;
        this.linesRows = linesRes.data.rows || [];
        this.linesCount = linesRes.data.count || 0;
      } catch (err) {
        console.error(err);
        this.$refs.Dialog.showToast("danger", "ไม่สามารถโหลดรายงานเงินมัดจำได้");
      } finally {
        this.isLoading = false;
      }
    },
    async loadLinesPage(page) {
      this.linesPage = page;
      this.linesBusy = true;
      try {
        const params = this.rangeParams();
        const offset = (page - 1) * this.linesPerPage;
        const res = await this.fetchBranchDepositLinesReport({
          ...params,
          offset,
          limit: this.linesPerPage,
        });
        this.linesRows = res.data.rows || [];
        this.linesCount = res.data.count || 0;
      } catch (err) {
        console.error(err);
        this.$refs.Dialog.showToast("danger", "ไม่สามารถโหลดรายการเงินมัดจำได้");
      } finally {
        this.linesBusy = false;
      }
    },
    // Builds a detached <table> (never attached to the page) carrying the 4
    // headline totals plus the single-branch movement row, so the "สรุป"
    // export sheet stays self-contained even though block 1 is its own card
    // row on screen, separate from the movement table's markup.
    buildSummarySheetTable() {
      const table = document.createElement("table");

      const thead = document.createElement("thead");
      const headRow = document.createElement("tr");
      this.movementFields.forEach(f => {
        const th = document.createElement("th");
        th.textContent = f.label;
        headRow.appendChild(th);
      });
      thead.appendChild(headRow);
      table.appendChild(thead);

      const tbody = document.createElement("tbody");

      const totalsCells = [
        `รับมัดจำ: ${this.formatNumber(this.movementTotals.deposited)}`,
        `ตัดใช้มัดจำ: ${this.formatNumber(this.movementTotals.deducted)}`,
        `ยกเลิกรับ: ${this.formatNumber(this.movementTotals.cancelledDeposit)}`,
        `คืนจากยกเลิก: ${this.formatNumber(this.movementTotals.restoredDeduction)}`,
      ];
      const totalsRow = document.createElement("tr");
      this.movementFields.forEach((f, idx) => {
        const td = document.createElement("td");
        td.textContent = totalsCells[idx] || "";
        totalsRow.appendChild(td);
      });
      tbody.appendChild(totalsRow);

      const spacerRow = document.createElement("tr");
      this.movementFields.forEach(() => spacerRow.appendChild(document.createElement("td")));
      tbody.appendChild(spacerRow);

      this.movementRows.forEach(row => {
        const tr = document.createElement("tr");
        this.movementFields.forEach(f => {
          const td = document.createElement("td");
          if (f.class && f.class.includes("text-right")) td.classList.add("text-right");
          let value = row[f.key];
          if (f.key === "patientCount") value = this.formatInt(value);
          else if (f.key !== "branchName") value = this.formatNumber(value);
          td.textContent = value == null ? "" : value;
          tr.appendChild(td);
        });
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);

      return table;
    },
    exportOptions() {
      const sheets = [
        { tableEl: this.buildSummarySheetTable(), name: "สรุป" },
        { tableId: "DepositLinesTable", name: "รายการ" },
      ];

      return {
        sheets,
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
      };
    },
    // The ledger table only ever holds one page of rows (loadLinesPage replaces
    // them on every page change). Exporting the DOM table as-is would silently
    // truncate the "รายการ" sheet to whatever page happened to be on screen, with
    // no indication anything was cut — wrong for a financial reconciliation
    // report. So export always fetches the FULL range first, waits for the table
    // to re-render, runs the export, then restores whatever page the user was on.
    async withFullLedgerForExport(action) {
      if (!this.movement) return;

      const previousPage = this.linesPage;
      this.isLoading = true;
      try {
        const params = this.rangeParams();
        const fullLimit = this.linesCount > 0 ? this.linesCount : 100000;
        const res = await this.fetchBranchDepositLinesReport({
          ...params,
          offset: 0,
          limit: fullLimit,
        });
        this.linesRows = res.data.rows || [];
        await this.$nextTick();
        action();
      } catch (err) {
        console.error(err);
        this.$refs.Dialog.showToast("danger", "ไม่สามารถเตรียมข้อมูลสำหรับ export ได้");
      } finally {
        await this.loadLinesPage(previousPage);
        this.isLoading = false;
      }
    },
    excelExport() {
      this.withFullLedgerForExport(() => {
        exportTableToExcel(this.exportOptions());
      });
    },
    pdfExport() {
      this.withFullLedgerForExport(() => {
        exportReportToPdf({ ...this.exportOptions(), autoFit: true });
      });
    },
  },
  mounted() {
    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }
  },
};
</script>

<style scoped>
.section-title {
  margin: 0 0 8px 0;
  font-weight: 600;
}
.summary-tile {
  background: #eaf3ff;
  border-color: #cfe2ff;
}
.summary-tile .tile-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  color: #495057;
  font-weight: 600;
}
.summary-tile .tile-num {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2d3d;
  margin-top: 2px;
}
</style>
